import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * SearchSelect — pemilih dengan pencarian (combobox). Saat dibuka, daftar
 * dirender INLINE (bukan dropdown absolut) agar tak terpotong di dalam Dialog
 * atau stacking context. Cocok untuk daftar kategori/relasi yang bisa panjang.
 */
export interface SearchSelectOption {
  value: string;
  label: string;
  hint?: string;
}

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = "— pilih —",
  searchPlaceholder = "Cari…",
  emptyText = "Tidak ada hasil.",
  disabled,
  ariaLabel,
  className,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
        setTerm("");
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, term]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
    setTerm("");
  }

  return (
    <div ref={ref} className={cn("relative", className)}>
      {open ? (
        <div className="rounded-md border border-border bg-bg">
          <div className="flex items-center gap-2 border-b border-border px-2">
            <Search className="size-4 shrink-0 text-muted" aria-hidden />
            <input
              autoFocus
              className="h-10 w-full bg-transparent text-sm text-ink outline-none focus-visible:shadow-none"
              placeholder={searchPlaceholder}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setOpen(false);
                  setTerm("");
                } else if (event.key === "Enter") {
                  // Enter tetap dalam combobox (cegah submit dialog di luar).
                  event.preventDefault();
                  if (filtered.length) choose(filtered[0].value);
                }
              }}
              aria-label={ariaLabel}
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length ? (
              filtered.map((option) => (
                <li key={option.value || "__empty"}>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-faint",
                      "focus-visible:bg-faint focus-visible:outline-none focus-visible:shadow-none",
                      option.value === value && "text-primary",
                    )}
                    onClick={() => choose(option.value)}
                  >
                    <span className="min-w-0 truncate">{option.label}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      {option.hint ? <span className="text-xs text-muted">{option.hint}</span> : null}
                      {option.value === value ? <Check className="size-4" aria-hidden /> : null}
                    </span>
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-muted">{emptyText}</li>
            )}
          </ul>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-label={ariaLabel}
          className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border bg-bg px-2 text-left text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:shadow-none disabled:opacity-50"
        >
          <span className={cn("min-w-0 truncate", !selected && "text-muted")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden />
        </button>
      )}
    </div>
  );
}
